import React, { useState } from 'react';
import Loading from '@gp/components/Loading';
import NoData from '@gp/components/NoData';
import CardSlot from '@gp/components/CardSlot';
import GeneralModal from '@gp/components/GeneralModal';
import EllipsisTextBubble from '@gp/components/EllipsisTextBubble';
import TextAreaMore from '@gp/components/TextAreaMore';
import { formatDateStr } from '@gp/utils/common';
import { DATA_MAP } from './utils/constants';

import type { IBaseProps } from '@home/types/common';

import './index.scss';

interface IAboutProps extends IBaseProps {
  loading: boolean;
  headerData: Record<string, any>;
  data: any;
}

const getRegionCountryValue = (data?: any): string => {
  const { location_city: region, location_country: country } = data || {};
  const regionTrim = region?.trim() || '';
  const countryTrim = country?.trim() || '';
  if (!regionTrim && !countryTrim) return '';
  if (!regionTrim) return countryTrim;
  if (!countryTrim) return regionTrim;
  return `${regionTrim}, ${countryTrim}`;
};

const getFieldValue = (data?: any, key?: string): string => {
  if (!key) return '';
  if (key === 'location_city/location_country') return getRegionCountryValue(data);
  return data?.[key] || '';
};

// 过滤掉无数据的字段
const getAvailableFields = (data?: any): any[] => {
  if (!data) return [];
  return DATA_MAP?.filter((field) => {
    const { key } = field || {};
    const value = getFieldValue(data, key);
    return !!value?.trim();
  });
};

const About: React.FC<IAboutProps> = (props) => {
  const { loading = false, headerData = null, data = null } = props;

  const description = headerData?.introduction?.trim() || data?.companyDetails?.description?.trim() || '';
  const updateTimeFormatted = formatDateStr(data?.companyDetails?.update_time, 'YYYY-MM');
  // 过滤掉无数据的字段
  const fields = getAvailableFields(data?.companyDetails);
  // 整个模块是否无数据
  const isNoData = !fields?.length && !description?.length;
  // 是否展示description弹窗
  const [visible, setVisible] = useState<boolean>(false);

  return (
    <CardSlot
      className="dbg-details-overview-about"
      title={(
        <div className='dbg-details-overview-about-title'>
          About
          {!!updateTimeFormatted && <span className='dbg-details-overview-about-subtitle'>Data Time: {updateTimeFormatted}</span>}
        </div>
      )}
    >
      {loading && <Loading className="dbg-details-overview-about-state-action" loading />}
      {!loading && isNoData && <NoData className="dbg-details-overview-about-state-action" />}
      {!loading && !isNoData && (
        <div className="overview-about-content font14">
          <div className="about-body">
            <div className="left-label">
              {fields?.map((t: { label: string; key: string; }, i: number) => (
                <label key={i} className="label-item">
                  {t?.label}
                </label>
              ))}
            </div>
            <div className="right-info">
              {fields?.map((t: { label: string; key: string; }, i: number) => {
                const text = getFieldValue(data?.companyDetails, t?.key);
                return (
                  <p key={i} className="info-item">
                    {t?.label === 'Webside' && (
                      <a href={text || undefined} target="_blank" rel="noreferrer">
                        <EllipsisTextBubble text={text || '-'} />
                      </a>
                    )}
                    {t?.label !== 'Webside' && (
                      <EllipsisTextBubble
                        text={text?.replaceAll('<SEP>', ',') || '-'}
                      />
                    )}
                  </p>
                );
              })}
            </div>
          </div>
          {!!description && (
            <div className="about-footer">
              <TextAreaMore
                className="gp-word-break-word"
                type="ellipsis"
                foldButtonText={description ? 'More' : ''}
                text={description}
                onClick={() => {
                  setVisible(true);
                }}
              />
            </div>
          )}
        </div>
      )}
      {/* Show All弹窗 */}
      <GeneralModal
        className="gp-word-break-word dbg-details-overview-about-modal"
        modalSize="m"
        visible={visible}
        title="About"
        content={description || '-'}
        isFooter={false}
        onClose={() => {
          setVisible(false);
        }}
      />
    </CardSlot>
  );
};

export default React.memo(About);
